import { useEffect, useState } from 'react'
import * as Sentry from '@sentry/react'

import { retryPromise } from 'shared/utils/repeatLoop'

// Extend the Window interface to include Google Maps API
declare global {
  interface Window {
    google?: {
      maps?: {
        places?: Record<string, unknown>
        [key: string]: unknown
      }
    }
  }
}

const getScript = () => {
  return document.querySelector(
    'script[src^="https://maps.googleapis.com/maps/api/js"]'
  )
}

const checkIsGoogleLoaded = () => {
  return (
    typeof window !== 'undefined' &&
    !!window.google?.maps &&
    !!window.google?.maps?.places
  )
}

const useGooglePlacesInit = () => {
  const [isLoaded, setIsLoaded] = useState<boolean | null>(false)

  useEffect(() => {
    if (isLoaded) {
      return
    }

    const script = getScript()

    if (!script) {
      const newScript = document.createElement('script')

      newScript.src = `https://maps.googleapis.com/maps/api/js?key=${
        process.env.NEXT_PUBLIC_GOOGLE_API_KEY || ''
      }&libraries=places&loading=async`
      newScript.async = true
      newScript.defer = true
      newScript.onload = () => {
        void retryPromise({
          maxRetries: 50,
          retryDelay: 300,
          check: checkIsGoogleLoaded
        }).then(() => {
          setIsLoaded(true)
          newScript.setAttribute('data-loaded', 'true')
        })
      }

      newScript.onerror = () => {
        console.error('Failed to load Google Maps JavaScript API')
        Sentry.captureException(
          new Error('Failed to load Google Maps JavaScript API')
        )
        setIsLoaded(false)
      }

      document.head.appendChild(newScript)

      return
    }

    void retryPromise({
      maxRetries: 50,
      retryDelay: 300,
      check: checkIsGoogleLoaded
    })
      .then(() => {
        // Double check that Places library is available
        if (checkIsGoogleLoaded()) {
          setIsLoaded(true)
          getScript()?.setAttribute('data-loaded', 'true')
        } else {
          console.error('Google Maps Places library not available')
          setIsLoaded(false)
        }
      })
      .catch(() => {
        console.error('Failed to load Google Maps Places library')
        setIsLoaded(false)
      })

    return () => {
      // Note: We don't remove the script on cleanup as it might be used by other components
      // Google Maps API is typically loaded once per application lifecycle
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return { isLoaded }
}

export default useGooglePlacesInit
